import { Copy, Download } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

export const StrappingTable = ({ open, onOpenChange, rows, unit, meta }) => {
  const header = ["Fill %", `Height (${unit})`, "Volume (L)"];
  const toText = (sep) =>
    [header.join(sep), ...rows.map((r) => [r.pct, r.height.toFixed(1), r.liters.toFixed(1)].join(sep))].join("\n");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(toText("\t"));
      toast.success("Strapping table copied to clipboard");
    } catch (err) {
      console.warn("Clipboard write failed:", err);
      toast.error("Couldn't access clipboard — use Download CSV instead");
    }
  };

  const handleCsv = () => {
    const blob = new Blob([toText(",")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tank-strapping-table.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#131924] border-[#243046] text-slate-200 max-w-2xl" data-testid="strapping-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-slate-100">Dipstick Strapping Table</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs font-mono">{meta}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button size="sm" data-testid="copy-table-btn" onClick={handleCopy} className="bg-amber-500 text-[#0B0F17] hover:bg-amber-400 font-semibold">
            <Copy />Copy Table
          </Button>
          <Button size="sm" variant="outline" data-testid="download-csv-btn" onClick={handleCsv} className="border-[#243046] bg-transparent text-slate-200 hover:bg-[#1A2232]">
            <Download />Download CSV
          </Button>
        </div>
        <div className="max-h-80 overflow-y-auto rounded-lg border border-[#243046]">
          <table className="w-full text-sm font-mono" data-testid="strapping-table">
            <thead className="bg-[#0D131E] sticky top-0">
              <tr>
                {header.map((h) => (
                  <th key={h} className="text-left px-4 py-2 text-xs uppercase tracking-wider text-slate-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.pct} className="border-t border-[#243046]/60" data-testid={`strapping-row-${r.pct}`}>
                  <td className="px-4 py-1.5 text-amber-400">{r.pct}%</td>
                  <td className="px-4 py-1.5">{r.height.toFixed(1)}</td>
                  <td className="px-4 py-1.5">{r.liters.toLocaleString("en-US", { maximumFractionDigits: 1 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};
